/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { FiToggleLeft, FiToggleRight } from "react-icons/fi";
import WhatsLayout from "../layouts/WhatsLayout";
import { useUI } from "../theme/ThemeProvider";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import { useTenantStorePermissions } from "../../hooks/useTenantStorePermissions";
import NoAccess from "../components/NoAccess";

// Using colors from useUI instead of hardcoded COLORS

export default function AdminPaymentMethods() {
  const { theme, lang, colors } = useUI();
  const isDark = theme === "dark";
  const isArabic = lang === "ar";
  const perms = useTenantStorePermissions();
  const baseUrl = (process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq/").replace(/\/+$/, '');
  const tenantId = parseInt(localStorage.getItem("tenantId") || "0");
  console.log(tenantId);

  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tenantPaymentMethods, setTenantPaymentMethods] = useState([]);
  const [loadingTenantPayment, setLoadingTenantPayment] = useState(true);

  // Fetch tenant payment methods from GetTenantPayment API
  useEffect(() => {
    const fetchTenantPayment = async () => {
      if (!perms.viewPaymentMethods) {
        return;
      }
      if (!tenantId || tenantId === -1) {
        setLoadingTenantPayment(false);
        return;
      }

      try {
        setLoadingTenantPayment(true);
        const token = localStorage.getItem("authToken");



        const res = await fetch(
          `${baseUrl}/GetTenantPayment?tenantId=${tenantId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              "Accept": "application/json",
              "Authorization": token ? `Bearer ${token}` : "",
            },
          }
        );

        let data = [];
        const contentType = res.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          try {
            data = await res.json();
          } catch (parseError) {
            const text = await res.text();
            console.error("Failed to parse GetTenantPayment JSON response:", parseError);
            console.error("Response text:", text);
            if (text) {
              try {
                data = JSON.parse(text);
              } catch {
                data = [];
              }
            }
          }
        } else {
          const text = await res.text();
          if (text) {
            try {
              data = JSON.parse(text);
            } catch {
              data = [];
            }
          }
        }

        if (!res.ok) {
          const errorMessage = data?.message || data?.error || data?.Message || `HTTP ${res.status}: ${res.statusText}`;
          throw new Error(errorMessage);
        }

        if (Array.isArray(data)) {
          // Map API response to our format
          const mappedMethods = data.map((m) => {
            // Handle engPaymentMethodName if it's an object
            let engName = "";
            if (typeof m.engPaymentMethodName === 'string') {
              engName = m.engPaymentMethodName;
            } else if (m.engPaymentMethodName && typeof m.engPaymentMethodName === 'object') {
              engName = m.paymentMethodName || "";
            }

            return {
              id: m.id || 0,
              paymentMethodId: m.paymentMethodId || 0,
              name: isArabic
                ? (m.paymentMethodName || "—")
                : (engName || m.paymentMethodName || "—"),
              description: isArabic
                ? "طريقة دفع متاحة"
                : "Available payment method",
              active: m.isActive === 1 || m.isActive === true || m.active === true,
              icon: "💳",
              paymentMethodName: m.paymentMethodName || "",
              engPaymentMethodName: engName || m.paymentMethodName || "",
            };
          });

          setTenantPaymentMethods(mappedMethods);
          console.log("✅ GetTenantPayment Response:", mappedMethods);
        } else {
          console.warn("Expected array but got:", typeof data);
          setTenantPaymentMethods([]);
        }
      } catch (err) {
        console.error("GetTenantPayment error:", err);
        setTenantPaymentMethods([]);
      } finally {
        setLoadingTenantPayment(false);
      }
    };

    if (tenantId && tenantId !== -1) {
      fetchTenantPayment();
    } else {
      setLoadingTenantPayment(false);
    }
  }, [baseUrl, tenantId, isArabic]);

  // Fetch payment methods from API
  useEffect(() => {
    const fetchPaymentMethods = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("authToken");

        // Use proxy in development, full URL in production


        const res = await fetch(
          `${baseUrl}/PaymentMethods/GetPaymentMethods?TenantId=-1`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              "Accept": "application/json",
              "Authorization": token ? `Bearer ${token}` : "",
            },
          }
        );

        // Try to parse response
        let data = [];
        const contentType = res.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          try {
            data = await res.json();
          } catch (parseError) {
            const text = await res.text();
            console.error("Failed to parse JSON response:", parseError);
            console.error("Response text:", text);
            throw new Error("Invalid response format");
          }
        } else {
          const text = await res.text();
          if (text) {
            try {
              data = JSON.parse(text);
            } catch {
              throw new Error("Invalid response format");
            }
          }
        }

        if (!res.ok) {
          const errorMessage = data?.message || data?.error || data?.Message || `HTTP ${res.status}: ${res.statusText}`;
          throw new Error(errorMessage);
        }

        if (Array.isArray(data)) {
          // Filter by current tenantId
          const tenantMethods = data.filter((m) => {
            const methodTenantId = m.tenantId || m.TenantId || 0;
            return methodTenantId === tenantId;
          });

          // Group by paymentMethodId to get unique methods (take the most recent one if duplicates)
          const methodMap = new Map();
          tenantMethods.forEach((m) => {
            const methodId = m.paymentMethodId || m.id || 0;
            if (methodId > 0) {
              // If method already exists, keep the one with higher id (more recent)
              const existing = methodMap.get(methodId);
              if (!existing || (m.id || 0) > (existing.id || 0)) {
                methodMap.set(methodId, m);
              }
            }
          });

          // Map API response to our format
          const mappedMethods = Array.from(methodMap.values()).map((m) => {
            // Handle engPaymentMethodName if it's an object
            let engName = "";
            if (typeof m.engPaymentMethodName === 'string') {
              engName = m.engPaymentMethodName;
            } else if (m.engPaymentMethodName && typeof m.engPaymentMethodName === 'object') {
              engName = m.paymentMethodName || "";
            }

            return {
              id: m.id || 0,
              paymentMethodId: m.paymentMethodId || 0,
              name: isArabic
                ? (m.paymentMethodName || "—")
                : (engName || m.paymentMethodName || "—"),
              description: isArabic
                ? "طريقة دفع متاحة"
                : "Available payment method",
              active: m.isActive === 1 || m.isActive === true || m.active === true,
              icon: "💳",
              paymentMethodName: m.paymentMethodName || "",
              engPaymentMethodName: engName || m.paymentMethodName || "",
            };
          });

          setMethods(mappedMethods);
          console.log("✅ GetPaymentMethods Response (filtered by tenantId):", mappedMethods);
        } else {
          console.warn("Expected array but got:", typeof data);
          setMethods([]);
        }
      } catch (err) {
        console.error("GetTenantPayment error:", err);

        let errorMessage = err.message || String(err);

        // Handle specific error cases
        if (errorMessage.includes("Failed to fetch") || errorMessage.includes("NetworkError")) {
          errorMessage = isArabic
            ? "فشل الاتصال بالخادم. يرجى التحقق من اتصال الإنترنت"
            : "Connection failed. Please check your internet connection";
        } else if (errorMessage.includes("401") || errorMessage.toLowerCase().includes("unauthorized")) {
          errorMessage = isArabic
            ? "غير مصرح. يرجى تسجيل الدخول مرة أخرى"
            : "Unauthorized. Please login again";
        } else if (errorMessage.includes("404")) {
          errorMessage = isArabic
            ? "لا توجد طرق دفع متاحة"
            : "No payment methods found";
        } else if (errorMessage.includes("500")) {
          errorMessage = isArabic
            ? "خطأ في الخادم. يرجى المحاولة لاحقاً"
            : "Server error. Please try again later";
        }

        setMethods([]);
      } finally {
        setLoading(false);
      }
    };

    if (tenantId && tenantId !== -1) {
      fetchPaymentMethods();
    } else {
      setLoading(false);
    }
  }, [baseUrl, tenantId, isArabic]);

  const toggleActive = async (id, isTenantPayment = false) => {
    const methodList = isTenantPayment ? tenantPaymentMethods : methods;
    const method = methodList.find((m) => m.id === id);
    if (!method) return;

    const newActiveStatus = !method.active;

    // Optimistically update UI
    if (isTenantPayment) {
      setTenantPaymentMethods((prev) =>
        prev.map((m) => (m.id === id ? { ...m, active: newActiveStatus } : m))
      );
    } else {
      setMethods((prev) =>
        prev.map((m) => (m.id === id ? { ...m, active: newActiveStatus } : m))
      );
    }

    try {
      const token = localStorage.getItem("authToken");

      // Use proxy in development, full URL in production


      // Prepare payload for SavePaymentMethods API according to API specification
      const payload = [
        {
          tenantId: tenantId,
          paymentMethodId: method.paymentMethodId || method.id || 0,
          paymentMethodName: (method.paymentMethodName || method.name || "").trim(),
          engPaymentMethodName: (method.engPaymentMethodName || method.name || "").trim(),
          isActive: newActiveStatus ? 1 : 0,
        },
      ];

      const res = await fetch(`${baseUrl}/PaymentMethods/SavePaymentMethods`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "Authorization": token ? `Bearer ${token}` : "",
        },
        body: JSON.stringify(payload),
      });

      // Try to parse response
      let data = {};
      const contentType = res.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        try {
          data = await res.json();
        } catch (parseError) {
          const text = await res.text();
          console.error("Failed to parse JSON response:", parseError);
          console.error("Response text:", text);
          throw new Error("Invalid response format");
        }
      } else {
        const text = await res.text();
        if (text) {
          try {
            data = JSON.parse(text);
          } catch {
            data = { message: text || `HTTP ${res.status}: ${res.statusText}` };
          }
        }
      }

      if (!res.ok) {
        const errorMessage = data?.message || data?.error || data?.Message || (isArabic
          ? "فشل تحديث طريقة الدفع ❌"
          : "Failed to update payment method ❌");

        if (data?.errors) {
          const validationErrors = Object.entries(data.errors)
            .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(", ") : value}`)
            .join("\n");
          console.error("❌ Validation Errors:", validationErrors);
          throw new Error(isArabic
            ? `خطأ في التحقق من البيانات:\n${validationErrors}`
            : `Validation Error:\n${validationErrors}`);
        }

        throw new Error(errorMessage);
      }

      console.log("✅ SavePaymentMethods Response:", data);

      Swal.fire({
        icon: "success",
        title: isArabic ? "تم الحفظ! ✅" : "Saved! ✅",
        timer: 1500,
        showConfirmButton: false,
        text: data?.message || (isArabic
          ? "تم تحديث طريقة الدفع بنجاح"
          : "Payment method updated successfully"),
        confirmButtonColor: colors.mainColor,
        background: isDark ? "#1E293B" : "#fff",
        color: isDark ? "#fff" : "#000",
      });
    } catch (err) {
      console.error("SavePaymentMethods error:", err);

      // Revert UI change on error
      if (isTenantPayment) {
        setTenantPaymentMethods((prev) =>
          prev.map((m) => (m.id === id ? { ...m, active: !newActiveStatus } : m))
        );
      } else {
        setMethods((prev) =>
          prev.map((m) => (m.id === id ? { ...m, active: !newActiveStatus } : m))
        );
      }

      let errorMessage = err.message || String(err);

      // Handle specific error cases
      if (errorMessage.includes("Failed to fetch") || errorMessage.includes("NetworkError")) {
        errorMessage = isArabic
          ? "فشل الاتصال بالخادم. يرجى التحقق من اتصال الإنترنت"
          : "Connection failed. Please check your internet connection";
      } else if (errorMessage.includes("401") || errorMessage.toLowerCase().includes("unauthorized")) {
        errorMessage = isArabic
          ? "غير مصرح. يرجى تسجيل الدخول مرة أخرى"
          : "Unauthorized. Please login again";
      } else if (errorMessage.includes("400")) {
        errorMessage = isArabic
          ? "بيانات غير صحيحة. يرجى التحقق من الحقول المطلوبة"
          : "Invalid data. Please check required fields";
      } else if (errorMessage.includes("404")) {
        errorMessage = isArabic
          ? "طريقة الدفع غير موجودة"
          : "Payment method not found";
      } else if (errorMessage.includes("500")) {
        errorMessage = isArabic
          ? "خطأ في الخادم. يرجى المحاولة لاحقاً"
          : "Server error. Please try again later";
      }

      Swal.fire({
        icon: "error",
        title: isArabic ? "فشل التحديث ❌" : "Update Failed ❌",
        text: errorMessage,
        confirmButtonColor: colors.mainColor,
        background: isDark ? "#1E293B" : "#fff",
        color: isDark ? "#fff" : "#000",
      });
    }
  };

  if (!perms.viewPaymentMethods) {
    return <NoAccess />;
  }
  return (
    <WhatsLayout title={isArabic ? "طرق الدفع" : "Payment Methods"}>
      <section
        dir={isArabic ? "rtl" : "ltr"}
        className={`min-h-screen px-5 sm:px-10 py-14 transition-all duration-300 ${isDark ? "text-gray-100" : "text-gray-900"
          }`}
        style={{
          background: isDark
            ? "linear-gradient(180deg, #0B1020 0%, #131827 100%)"
            : colors.bg,
        }}
      >

        {/* ===== طرق الدفع (GetTenantPayment) ===== */}
        {perms.viewPaymentMethods && (
          <div className="mb-20">
            <h3
              className="text-2xl font-semibold mb-10 text-center"
              style={{ color: colors.mainColor }}
            >
              {isArabic ? "طرق الدفع" : "Payment Methods"}
            </h3>

            {loadingTenantPayment ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2" style={{ borderColor: colors.mainColor }}></div>
                <p className="mt-4 text-gray-400">
                  {isArabic ? "جاري تحميل طرق الدفع..." : "Loading payment methods..."}
                </p>
              </div>
            ) : tenantPaymentMethods.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-400">
                  {isArabic ? "لا توجد طرق دفع متاحة" : "No payment methods available"}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {tenantPaymentMethods.map((m) => (
                  <motion.div
                    key={m.id}
                    whileHover={{ scale: 1.03, y: -5 }}
                    transition={{ type: "spring", stiffness: 250 }}
                    className={`relative p-8 rounded-3xl border shadow-2xl transition-all duration-300 backdrop-blur-md ${isDark
                      ? "bg-[#1E293B]/70 border-[#334155]"
                      : "bg-white/90 border-gray-200"
                      }`}
                  >
                    <div className="flex justify-center text-5xl mb-4">{m.icon}</div>
                    <h3
                      className="text-xl font-bold text-center mb-2"
                      style={{ color: colors.mainColor }}
                    >
                      {m.name}
                    </h3>
                    <p className="text-center text-sm opacity-70 mb-6">
                      {m.description}
                    </p>
                    <div className="flex justify-center items-center mt-auto">
                      <button
                        onClick={perms.editPaymentMethods ? () => toggleActive(m.id, true) : null}
                        className="flex items-center gap-2 text-sm font-semibold transition-transform"
                        style={{
                          color: m.active ? colors.green : colors.red,
                        }}
                      >
                        {m.active ? (
                          <>
                            <FiToggleRight size={24} />{" "}
                            {isArabic ? "مفعل" : "Active"}
                          </>
                        ) : (
                          <>
                            <FiToggleLeft size={24} />{" "}
                            {isArabic ? "غير مفعل" : "Inactive"}
                          </>
                        )}
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ===== طرق الدفع من مدار ===== */}
        {perms.viewPaymentMethods && (
          <>
            <div className="text-center">
              <h3
                className="text-2xl font-semibold mb-10"
                style={{ color: colors.gold }}
              >
                {isArabic ? "بوابات الدفع عبر مدار" : "Payment Gateways by Madar"}
              </h3>

              {loading ? (
                <div className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2" style={{ borderColor: colors.mainColor }}></div>
                  <p className="mt-4 text-gray-400">
                    {isArabic ? "جاري تحميل طرق الدفع..." : "Loading payment methods..."}
                  </p>
                </div>
              ) : methods.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-400">
                    {isArabic ? "لا توجد طرق دفع متاحة" : "No payment methods available"}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 justify-center">
                  {methods.map((m) => (
                    <motion.div
                      key={m.id}
                      whileHover={{ scale: 1.05, y: -5 }}
                      transition={{ type: "spring", stiffness: 250 }}
                      className={`relative p-8 rounded-3xl border shadow-2xl transition-all duration-300 backdrop-blur-md ${isDark
                        ? "bg-[#1E293B]/70 border-[#334155]"
                        : "bg-white/90 border-gray-200"
                        }`}
                    >
                      <div className="flex justify-center text-5xl mb-4">{m.icon}</div>
                      <h4
                        className="text-lg font-bold text-center mb-2"
                        style={{ color: colors.green }}
                      >
                        {m.name}
                      </h4>
                      <p className="text-center text-sm opacity-70">
                        {m.description}
                      </p>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </section>
    </WhatsLayout>
  );
}
